import React, { useEffect, useState } from "react";
import { Upload, Table, Input, Button, Space, Flex, Modal, message } from "antd";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import type { UploadProps } from "antd/es/upload";
import { Typography } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
const { Text } = Typography;
import { saveAs } from "file-saver";

interface SampleData {
    sampleData: string[][];
    onDataload: (data: any[]) => void;
}

const ParseCsv: React.FC<SampleData> = ({ sampleData, onDataload }) => {
    const [data, setData] = useState<any[]>([]);

    const props: UploadProps = {
        beforeUpload: (file) => {
            const fileName = file.name.toLowerCase();
            const isExcelFile = fileName.endsWith('.xlsx') || fileName.endsWith('.xls') ||
                file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                file.type === 'application/vnd.ms-excel';
            const isCSVFile = fileName.endsWith('.csv') || file.type === 'text/csv' || file.type === 'application/vnd.ms-excel';

            // Validate file type - accept both CSV and Excel files
            if (!isExcelFile && !isCSVFile) {
                message.error('Please upload a CSV or Excel file (.csv, .xlsx, or .xls)');
                return false; // Prevent upload
            }

            const reader = new FileReader();

            if (isExcelFile) {
                // Handle Excel files
                reader.onload = (e) => {
                    try {
                        const data = new Uint8Array(e?.target?.result as ArrayBuffer);
                        const workbook = XLSX.read(data, { type: 'array' });

                        // Get the first sheet
                        const firstSheetName = workbook.SheetNames[0];
                        const worksheet = workbook.Sheets[firstSheetName];

                        // Convert to JSON with header row
                        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                            header: 1,
                            defval: '' // Default value for empty cells
                        });

                        if (jsonData.length === 0) {
                            message.warning('Excel file appears to be empty');
                            setData([]);
                            return;
                        }

                        // First row is headers
                        const headers = (jsonData[0] as any[]).map((h: any) => String(h || '').trim()).filter((h: string) => h);

                        if (headers.length === 0) {
                            message.warning('Excel file has no headers. Please ensure the first row contains column names.');
                            setData([]);
                            return;
                        }

                        // Normalize headers to match expected format (Question, Level, Skills)
                        const normalizedHeaders = headers.map((h: string) => {
                            const lower = h.toLowerCase().trim();
                            // Handle various header name variations - check if header contains key words
                            if (lower.includes('question') || lower === 'q' || lower === 'ques') return 'Question';
                            if (lower.includes('level') || lower.includes('difficulty') || lower === 'lvl') return 'Level';
                            if (lower.includes('skill')) return 'Skills';
                            return h; // Keep original if no match
                        });

                        console.log('Excel headers:', headers);
                        console.log('Normalized headers:', normalizedHeaders);

                        // Convert rows to objects with normalized headers
                        const parsedData = jsonData.slice(1).map((row: any[], rowIndex: number) => {
                            const rowObj: any = {};

                            // First, set values using normalized headers
                            normalizedHeaders.forEach((header, index) => {
                                const value = row[index] !== undefined ? String(row[index] || '').trim() : '';
                                // Always set the value, even if empty (to ensure all columns are present)
                                rowObj[header] = value;
                            });

                            // Then, ensure normalized keys are set from original headers (for cases where normalization didn't match)
                            headers.forEach((originalHeader, index) => {
                                const lower = originalHeader.toLowerCase().trim();
                                const value = String(row[index] || '').trim();
                                if (value) {
                                    if (lower.includes('question') || lower === 'q' || lower === 'ques') {
                                        rowObj['Question'] = rowObj['Question'] || value;
                                    }
                                    if (lower.includes('level') || lower.includes('difficulty') || lower === 'lvl') {
                                        rowObj['Level'] = rowObj['Level'] || value;
                                    }
                                    if (lower.includes('skill')) {
                                        rowObj['Skills'] = rowObj['Skills'] || value;
                                    }
                                }
                            });

                            // Debug first row
                            if (rowIndex === 0) {
                                console.log('Excel first row parsed:', {
                                    originalHeaders: headers,
                                    normalizedHeaders: normalizedHeaders,
                                    rowData: row,
                                    finalRowObj: rowObj
                                });
                            }

                            return rowObj;
                        });

                        console.log('Parsed data sample:', parsedData[0]);

                        // Filter out empty rows
                        const filteredData = parsedData.filter((row: any) => {
                            return Object.values(row).some((val: any) => val && val.toString().trim() !== '');
                        });

                        if (filteredData.length === 0) {
                            message.warning('Excel file appears to be empty or has no valid data');
                            setData([]);
                        } else {
                            setData(filteredData);
                            message.success(`Successfully loaded ${filteredData.length} row(s) from Excel`);
                        }
                    } catch (error) {
                        console.error('Error parsing Excel file:', error);
                        message.error('Failed to parse Excel file. Please check the file format.');
                        setData([]);
                    }
                };

                reader.onerror = () => {
                    message.error('Error reading Excel file. Please try again.');
                    setData([]);
                };

                reader.readAsArrayBuffer(file);
            } else {
                // Handle CSV files
                reader.onload = (e) => {
                    try {
                        const csvData = Papa.parse(e?.target?.result as string, {
                            header: true,
                            skipEmptyLines: true,
                            transformHeader: (header) => {
                                const trimmed = header.trim();
                                const lower = trimmed.toLowerCase();
                                // Normalize headers to match expected format - check if header contains key words
                                if (lower.includes('question') || lower === 'q' || lower === 'ques') return 'Question';
                                if (lower.includes('level') || lower.includes('difficulty') || lower === 'lvl') return 'Level';
                                if (lower.includes('skill')) return 'Skills';
                                return trimmed; // Keep original if no match
                            }
                        }).data;

                        console.log('CSV parsed data sample:', csvData[0]);
                        console.log('CSV data keys:', csvData.length > 0 ? Object.keys(csvData[0]) : 'empty');

                        // Filter out empty rows
                        const filteredData = csvData.filter((row: any) => {
                            return Object.values(row).some((val: any) => val && val.toString().trim() !== '');
                        });

                        if (filteredData.length === 0) {
                            message.warning('CSV file appears to be empty or has no valid data');
                            setData([]);
                        } else {
                            setData(filteredData);
                            message.success(`Successfully loaded ${filteredData.length} row(s) from CSV`);
                        }
                    } catch (error) {
                        console.error('Error parsing CSV:', error);
                        message.error('Failed to parse CSV file. Please check the file format.');
                        setData([]);
                    }
                };

                reader.onerror = () => {
                    message.error('Error reading CSV file. Please try again.');
                    setData([]);
                };

                reader.readAsText(file);
            }

            return false; // Prevent upload
        },
        accept: '.csv,.xlsx,.xls',
    };

    useEffect(() => {
        onDataload(data);
    }, [data]);

    const handleEdit = (value: string, record: any, column: string) => {
        const newData = data.map((item) =>
            item === record ? { ...item, [column]: value } : item
        );
        setData(newData);
    };

    const onDelete = (record) => {
        Modal.confirm({
            title: `Are you sure you want to delete this record?`,
            content: Object.keys(record).map((key) => key + ":" + record[key]).join(" | "),
            onOk: () => removeRecord(record),
        });
    }

    const removeRecord = (record) => {
        const newData = data.filter((item) => item.Sr !== record.Sr);
        setData(newData);
    }

    const action = () => {
        return {
            title: 'Action',
            dataIndex: '',
            key: 'action',
            width: 150,
            fixed: 'right',
            render: (text, record) => (
                <Space size="middle">
                    <Button type="link" icon={<DeleteOutlined />} onClick={() => onDelete(record)} danger />
                </Space>
            ),
        }
    };

    const columns = data.length
        ? Object.keys(data[0]).map((col) => ({
            title: col,
            dataIndex: col,
            render: (text: string, record: any) => (
                <Text editable={{
                    onChange: (value) => handleEdit(value, record, col),
                    triggerType: ['text']
                }} >{text}</Text>
            ),
        }))
        : [];

    columns.push(action());


    const generateSampleCSV = () => {
        if (sampleData.length === 0) return;
        const csvContent = sampleData.map(e => e.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        saveAs(blob, "sample_data.csv");
    };

    return (
        <div>
            <Flex justify="space-between" className="mb-4">
                <Upload {...props} showUploadList={false}>
                    <Button>Upload CSV/Excel</Button>
                </Upload>
                <Button type="link" onClick={generateSampleCSV}>
                    Download CSV Template
                </Button>
            </Flex>
            {data.length > 0 && <Table columns={columns} dataSource={data} pagination={false} rowKey={(r) => JSON.stringify(r)} />}
        </div>
    );
};

export default ParseCsv;
